import { getAccessToken, getSubscriptionToken } from '@/shared/tools/jwt.ts';
import { getEnv } from '@/shared/utils/get-env.ts';
import {
  ApolloClient, ApolloLink, createHttpLink, InMemoryCache,
  split,
} from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import {
  apolloAuthLink, apolloErrorLink,
  apolloRefreshTokenLink,
  apolloRequestIdLink,
} from './links.ts';
import result from '../graphql/possible-types';

const wsLink = new GraphQLWsLink(createClient({
  url: getEnv('VITE_APP_GQL_WS'),
  connectionParams: () => {
    return {
      authorization: `Bearer ${getSubscriptionToken() ?? getAccessToken()}`,
    };
  },
  keepAlive: 10_000, // ping server every 10 seconds
}));

const httpLink = createHttpLink({
  uri: getEnv('VITE_APP_GQL'),
});

const addQueryNameLink = new ApolloLink((operation, forward) => {
  const queryName = operation.operationName;

  operation.setContext(() => ({
    uri: `${getEnv('VITE_APP_GQL')}?operation=${queryName}`,
  }));

  return forward(operation);
});

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  apolloRefreshTokenLink
    .concat(apolloAuthLink)
    .concat(apolloRequestIdLink)
    .concat(addQueryNameLink)
    .concat(httpLink),
);

export const apolloClient = new ApolloClient({
  link: apolloErrorLink
    .concat(splitLink),
  cache: new InMemoryCache({
    possibleTypes: result.possibleTypes,
  }),
  //  TODO: implement GQL cache
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});
